import { Button, Input, Modal, notification, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../application/store';
import { useOrder } from '../../../../../hooks/useOrder';
import { useProduct } from '../../../../../hooks/useProduct';
import BVSpinLoader from '../../../../BVSpinLoader/bv-spin-loader';

interface IBVPasteExcelDialog {
	visible: boolean;
	onClose: () => void;
}

export interface IPasteExcelDialogForm {
	pasted?: string;
	listDataSource?: any[];
}

const BVPasteExcelDialog: React.FunctionComponent<IBVPasteExcelDialog> = ({
	visible,
	onClose,
}) => {
	const { TextArea } = Input;
	const [pastedText, setPastedText] = useState('');
	const [validated, setValidated] = useState(false);
	const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);
	const bvCustomer = useSelector((state: RootState) => state.customer);
	const [facilityId, setFacilityId] = useState<number | null>(null);
	const [pendingClose, setPendingClose] = useState(false);

	const { handleFacilities, facilities, facilitiesLoading } = useProduct();

	const {
		handleRetrieveOrder,
		handleUpsertOrder,
		upsertOrderLoading,
		retrieveOrderLoading,
	} = useOrder();
	useEffect(() => {
		if (bvCustomer.id) handleFacilities(bvCustomer.id);
	}, []);

	useEffect(() => {
		if (pendingClose && !upsertOrderLoading && !retrieveOrderLoading) {
			onClose();
			setPendingClose(false);
		}
	}, [pendingClose, upsertOrderLoading, retrieveOrderLoading, onClose]);

	const parsePastedText = (text: string) => {
		return text
			.split(/\r?\n/)
			.map((line) => line.trim())
			.filter(Boolean)
			.map((line) => {
				const [sapCode, qty] = line.split(/\s+/);

				const parsedQty = Number(qty);

				// scarta header o righe non valide
				if (!sapCode || Number.isNaN(parsedQty)) return null;

				return {
					sapCode,
					qty: parsedQty,
				};
			})
			.filter((p): p is { sapCode: string; qty: number } => p !== null);
	};

	const validatePasted = (paste: string) => {
		setPastedText(paste);

		const rows = paste
			.split(/\r?\n/)
			.map((r) => r.trim())
			.filter(Boolean);

		if (!rows.length) {
			setValidated(false);
			return;
		}

		const isValid = rows.every((row) => {
			const [sapCode, qty] = row.split(/\s+/);
			return sapCode && qty && !Number.isNaN(Number(qty));
		});

		if (!isValid) {
			setPastedText('');
			notification.error({
				message: 'Pasted excel text validation Error!',
				description: 'Invalid format or header detected.',
				duration: 2,
			});
		}

		setValidated(isValid);
	};

	const _handelUploadPastedExcel = async () => {
		if (validated && pastedText && pastedText !== '' && facilityId) {
			const products = parsePastedText(pastedText);
			handleUpsertOrder(
				{
					...bvOrderSummary,
					importedProducts: products,
					importedProductsFacility: facilities?.filter(
						(f) => f.id === facilityId,
					)[0],
				},
				(orderId) => handleRetrieveOrder(orderId),
			);
		}
		setPendingClose(true);
	};

	return (
		<Modal
			width={600}
			visible={visible}
			closable={false}
			maskClosable={false}
			title={`Paste here SapCode and quantity from your excel file`}
			destroyOnClose={true}
			footer={[
				<Button key="0" onClick={onClose} disabled={pendingClose}>
					Cancel
				</Button>,
				<Button
					key="1"
					type="primary"
					disabled={
						!facilityId ||
						!validated ||
						!pastedText ||
						pastedText === '' ||
						pendingClose
					}
					onClick={_handelUploadPastedExcel}
				>
					Upload
				</Button>,
			]}
			afterClose={() => {
				const txtArea = document.querySelector('body > textarea');
				txtArea?.remove();
			}}
		>
			{retrieveOrderLoading || upsertOrderLoading ? (
				<BVSpinLoader />
			) : (
				<>
					{' '}
					<Select
						loading={facilitiesLoading}
						options={(facilities ?? []).map((f) => ({
							label: `${f.name} - ${f.address}, ${f.city}`,
							value: Number(f.id),
						}))}
						placeholder="Ship to"
						value={facilityId ?? undefined}
						onChange={(value) => setFacilityId(value)}
						style={{ marginBottom: '2%' }}
					/>
					<TextArea
						onChange={(event) => validatePasted(event.target.value)}
						name="pastedExcel"
						value={pastedText}
						autoSize={{ minRows: 2, maxRows: 6 }}
					/>
				</>
			)}
		</Modal>
	);
};

export default BVPasteExcelDialog;
